import React, { useState, useEffect, useRef, useCallback } from 'react';
import AsyncSelectDropdown from './AsyncSelectDropdown';

interface AsyncSelectProps {
  label: string;
  name: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  fetchOptions: (query: string, page: number) => Promise<{ data: any[]; meta?: any }>;
  getOptionLabel: (item: any) => string;
  getOptionValue: (item: any) => string;
  pageSize?: number;
  /** Called with the full item object when an option is selected */
  onSelectItem?: (item: any) => void;
}

const AsyncSelect: React.FC<AsyncSelectProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  required,
  placeholder = 'Select...',
  disabled,
  fetchOptions,
  getOptionLabel,
  getOptionValue,
  pageSize = 20,
  onSelectItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState('');
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load options
  const loadOptions = useCallback(async (query: string = '', page: number = 1, append: boolean = false) => {
    try {
      setIsLoading(true);
      const response = await fetchOptions(query, page);
      
      if (append) {
        setOptions(prev => [...prev, ...response.data]);
      } else {
        setOptions(response.data);
      }
      
      // Check if there are more pages
      if (response.meta) {
        const { pagination } = response.meta;
        setHasMore(pagination.page < pagination.pageCount);
      } else {
        setHasMore(response.data.length >= pageSize);
      }
    } catch (error) {
      console.error('Failed to load options:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchOptions, pageSize]);

  // Initial load
  useEffect(() => {
    if (isOpen) {
      loadOptions(searchQuery, 1);
      setCurrentPage(1);
    }
  }, [isOpen, loadOptions, searchQuery]);

  // Search with debounce
  useEffect(() => {
    if (!isOpen) return;
    
    const timeoutId = setTimeout(() => {
      loadOptions(searchQuery, 1);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isOpen, loadOptions]);

  // Load selected label
  useEffect(() => {
    if (value && options.length > 0) {
      const selected = options.find(opt => getOptionValue(opt) === value);
      if (selected) {
        setSelectedLabel(getOptionLabel(selected));
      }
    } else if (value && !selectedLabel) {
      loadOptions('', 1).then(() => {
        const selected = options.find(opt => getOptionValue(opt) === value);
        if (selected) {
          setSelectedLabel(getOptionLabel(selected));
        }
      });
    }
  }, [value, options, getOptionLabel, getOptionValue, loadOptions, selectedLabel]);

  // Close dropdown on outside click (panel lives in a portal, so check both)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = dropdownRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inTrigger && !inPanel) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadOptions(searchQuery, nextPage, true);
    }
  };

  const handleSelect = (item: any) => {
    const optionValue = getOptionValue(item);
    const optionLabel = getOptionLabel(item);
    onChange(optionValue);
    if (onSelectItem) {
      onSelectItem(item);
    }
    setSelectedLabel(optionLabel);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    if (onSelectItem) {
      onSelectItem(null);
    }
    setSelectedLabel('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onBlur={onBlur}
          disabled={disabled}
          className={`w-full pl-4 pr-14 py-3 text-left border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-ocean-500/20 focus:border-ocean-500 transition-all duration-300 ${
            error ? 'border-red-500' : 'border-gray-200'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50 focus:bg-white cursor-pointer'}`}
        >
          <span className={`block truncate ${selectedLabel ? 'text-gray-900' : 'text-gray-400'}`}>
            {selectedLabel || placeholder}
          </span>
        </button>

        {/* Clear button */}
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-9 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Dropdown arrow */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown menu (portal so it paints above all sections) */}
      {isOpen && (
        <AsyncSelectDropdown
          anchorRef={dropdownRef}
          panelRef={panelRef}
          searchInputRef={searchInputRef}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          options={options}
          isLoading={isLoading}
          hasMore={hasMore}
          value={value}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          onSelect={handleSelect}
          onLoadMore={handleLoadMore}
        />
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default AsyncSelect;


